import { useCallback, useState } from 'react';

/**
 * State that survives navigation and reload, kept in localStorage.
 *
 * Written for the contracts rail, whose open/closed state was plain `useState`
 * and therefore reset every single time you moved between Planning and
 * Financials — a collapse the user has to redo on every navigation is worse than
 * no collapse at all.
 *
 * Panel *widths* do not need this: `Panels` hands that to
 * react-resizable-panels' own `autoSaveId` persistence.
 *
 * Reads and writes are guarded because localStorage throws in private-mode
 * Safari and when a quota is exhausted; losing the preference is acceptable,
 * taking the page down with it is not.
 */
export const usePersistedState = <T>(
  key: string,
  fallback: T,
  isValid: (value: unknown) => value is T,
): [T, (next: T) => void] => {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored === null) return fallback;
      const parsed: unknown = JSON.parse(stored);
      return isValid(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  });

  const persist = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Preference is lost for this session; the UI still works.
      }
    },
    [key],
  );

  return [value, persist];
};

/** Type guard for the common boolean case. */
export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';
